import React from "react";
import {
    Box,
    Container,
    Typography,
    Button,
    Stack,
    useTheme,
    Grid,
    Paper,
    Avatar,
    Chip,
    IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import VerifiedIcon from "@mui/icons-material/Verified";
import PsychologyIcon from "@mui/icons-material/Psychology";
import CodeIcon from "@mui/icons-material/Code";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const RegisterLanding = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isDark = theme.palette.mode === "dark";

    const colors = {
        primary: "#e50914",
        secondary: "#6a0dad",
        accent: "#A380FF",
        success: "#00e676",
        glass: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.8)",
    };

    return (
        <Box sx={{ bgcolor: isDark ? "#080808" : "#fdfdfd", minHeight: "100vh", overflow: "hidden" }}>
            {/* 🌌 Dynamic Background */}
            <Box
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                    pointerEvents: "none",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "-10%",
                        left: "-10%",
                        width: "60vw",
                        height: "60vw",
                        background: `radial-gradient(circle, ${colors.primary}15 0%, transparent 70%)`,
                        filter: "blur(100px)",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "-10%",
                        right: "-10%",
                        width: "60vw",
                        height: "60vw",
                        background: `radial-gradient(circle, ${colors.secondary}15 0%, transparent 70%)`,
                        filter: "blur(100px)",
                    }}
                />
            </Box>

            {/* 🚀 Main Content */}
            <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 15 }, pb: 10, position: "relative", zIndex: 1 }}>
                <Grid container spacing={8} alignItems="center">
                    {/* Left Column: Hooks & Value Prop */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Stack spacing={3}>
                            <Box>
                                <Chip
                                    icon={<RocketLaunchIcon style={{ color: colors.primary, fontSize: 16 }} />}
                                    label="JOIN 25,000+ DEVELOPERS"
                                    sx={{
                                        fontWeight: 900,
                                        px: 1,
                                        bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
                                        border: `1px solid ${colors.primary}40`,
                                        letterSpacing: 2,
                                        mb: 3
                                    }}
                                />
                                <Typography
                                    variant="h1"
                                    fontWeight={900}
                                    sx={{
                                        fontSize: { xs: "3rem", md: "4.5rem" },
                                        lineHeight: 1,
                                        mb: 2,
                                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                    }}
                                >
                                    Launch Your <br /> Tech Career.
                                </Typography>
                                <Typography variant="h5" sx={{ opacity: 0.8, fontWeight: 400, maxWidth: 550 }}>
                                    Master FastAPI, React, and Agentic AI with the most production-ready curriculum on the internet.
                                </Typography>
                            </Box>

                            <Stack spacing={2}>
                                {[
                                    "Zero to One Professional Curriculum",
                                    "Real-World Production Projects",
                                    "Direct Mentorship & Community Access",
                                    "100% Free Forever"
                                ].map((text, i) => (
                                    <Stack key={i} direction="row" alignItems="center" spacing={2}>
                                        <CheckCircleOutlineIcon sx={{ color: colors.success }} />
                                        <Typography variant="body1" fontWeight={600} sx={{ opacity: 0.9 }}>{text}</Typography>
                                    </Stack>
                                ))}
                            </Stack>

                            <Box sx={{ pt: 2 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate("/signup")}
                                    sx={{
                                        px: 6,
                                        py: 2.5,
                                        fontSize: "1.2rem",
                                        fontWeight: 900,
                                        borderRadius: "15px",
                                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                        boxShadow: `0 15px 35px ${colors.primary}40`,
                                        transition: "transform 0.3s ease",
                                        "&:hover": { transform: "scale(1.05)" },
                                    }}
                                    endIcon={<ArrowForwardIcon />}
                                >
                                    Create Free Account
                                </Button>
                                <Typography variant="caption" sx={{ display: "block", mt: 2, opacity: 0.5, ml: 1 }}>
                                    No credit card required. Start building in 60 seconds.
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* Right Column: Visual Showcase */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Paper
                            elevation={24}
                            sx={{
                                p: 2,
                                borderRadius: 8,
                                background: isDark ? "rgba(255,255,255,0.03)" : "#fff",
                                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                                position: "relative",
                                transform: { md: "rotate(2deg)" },
                            }}
                        >
                            <Box
                                component="img"
                                src="/images/dashboard-preview.png" // User should provide this or I'll use a placeholder
                                alt="Platform Preview"
                                sx={{ width: "100%", borderRadius: 6, display: "block" }}
                                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=800&q=80" }}
                            />

                            {/* 🎖️ Social Floating Card */}
                            <Paper
                                sx={{
                                    position: "absolute",
                                    bottom: -40,
                                    left: -40,
                                    p: 3,
                                    borderRadius: 5,
                                    display: { xs: "none", md: "block" },
                                    boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
                                    border: `1px solid ${colors.primary}20`
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar src="https://api.dicebear.com/7.x/avataaars/svg?seed=Felix" />
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={900}>Arjun Verma</Typography>
                                        <Typography variant="caption" color="text.secondary">Placed at Swiggy</Typography>
                                    </Box>
                                    <VerifiedIcon sx={{ color: "#3498db", fontSize: 20 }} />
                                </Stack>
                            </Paper>
                        </Paper>
                    </Grid>
                </Grid>

                {/* 🏢 Company Wall Section */}
                <Box sx={{ mt: { xs: 15, md: 20 }, textAlign: "center" }}>
                    <Typography variant="overline" sx={{ letterSpacing: 5, opacity: 0.5 }}>OUR STUDENTS WORK AT</Typography>
                    <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        spacing={{ xs: 3, md: 8 }}
                        sx={{ mt: 4, flexWrap: "wrap", gap: 3, opacity: 0.4, grayscale: 1 }}
                    >
                        {["Google", "Amazon", "Microsoft", "Meta", "Swiggy", "Zomato", "Uber"].map(brand => (
                            <Typography key={brand} variant="h5" fontWeight={900}>{brand.toUpperCase()}</Typography>
                        ))}
                    </Stack>
                </Box>

                {/* 🏗️ Core Modules Preview */}
                <Box sx={{ mt: 15 }}>
                    <Grid container spacing={3}>
                        {[
                            { title: "React Frontend", icon: <CodeIcon />, color: colors.secondary },
                            { title: "FastAPI Backend", icon: <RocketLaunchIcon />, color: colors.primary },
                            { title: "Agentic AI", icon: <PsychologyIcon />, color: colors.accent },
                            { title: "FullStack Projects", icon: <PlayCircleFilledIcon />, color: colors.success },
                        ].map((item, i) => (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                                <Paper
                                    sx={{
                                        p: 4,
                                        borderRadius: 6,
                                        textAlign: "center",
                                        transition: "all 0.3s ease",
                                        cursor: "default",
                                        border: `1px solid transparent`,
                                        "&:hover": {
                                            borderColor: item.color,
                                            transform: "translateY(-5px)",
                                            boxShadow: `0 15px 30px ${item.color}15`
                                        }
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: `${item.color}15`, color: item.color, mx: "auto", mb: 2, width: 56, height: 56 }}>
                                        {item.icon}
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={800}>{item.title}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
};

export default RegisterLanding;
